import type { ApiFlowsExecutionsResponseType } from "@/types/entities/flows";
import { formatDateTime } from "@/utils/dates";
import { getExecutionBadge } from ".";
import {
  Badge,
  Button,
  Heading,
  Icon,
  TableData,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@elaniintc/goes-design-system";
import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { DataCenterConditionOperations } from "@/workflow/components/configurations/data-center/components/condition-operations";

const OPERATION_LABELS: Record<string, string> = {
  create: "Creación",
  read: "Lectura",
  update: "Actualización",
  delete: "Eliminación",
};

const SCOPE_FIELD_LABELS: Record<string, string> = {
  created_at: "Fecha de creación",
  updated_at: "Fecha de última edición",
};

// Build a map of all condition labels from the operations
const CONDITION_LABELS: Record<string, string> = Object.values(
  DataCenterConditionOperations,
).reduce(
  (acc, operations) => {
    operations.forEach((op) => {
      acc[op.name] = op.label;
    });
    return acc;
  },
  {} as Record<string, string>,
);

interface Props {
  execution: ApiFlowsExecutionsResponseType["data"][number]["node_executions"][number];
}

type FilterData = Array<{
  id: string;
  name: string;
  condition: string;
  value: string;
  type: string;
}>;

interface ScopeData {
  condition: string;
  name: string;
  type: string;
  value: string | null;
  connection?: string;
  field?: string;
}

interface ExecutionInputs {
  table: string;
  operation: string;
  source: string;
  isMultiple: boolean;
  limit: number;
  outputName: string;
  data: Record<string, unknown>;
  logic_operator: string;
  filters: FilterData | null;
  scope: ScopeData[] | null;
}

interface TableOutputs {
  data?: Array<Record<string, unknown>>;
  ids?: Array<string | number>;
  message?: string;
  success?: boolean;
  updated_count?: number;
  deleted_count?: number;
  id?: string | number;
}

// Helper function to extract inputs
const getExecutionInputs = (
  execution: Props["execution"],
): ExecutionInputs | null => {
  if (!execution) {
    return null;
  }
  if (!execution.inputs || execution.inputs.length === 0) {
    return null;
  }

  const inputsMap = new Map(
    execution.inputs.map((input) => [input.name, input.value]),
  );

  const rawData = inputsMap.get("data");
  let parsedData: Record<string, unknown> = {};

  if (rawData) {
    try {
      parsedData = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
    } catch {
      parsedData = {};
    }
  }

  // Extract filters
  const filtersInput = execution.inputs.find(
    (input) => input.type === "FILTER",
  );
  const filters = (filtersInput?.filters as FilterData) || null;

  let scope: ScopeData[] | null = null;
  const scopeInput = inputsMap.get("scope");
  if (scopeInput) {
    try {
      const parsedScope =
        typeof scopeInput === "string" ? JSON.parse(scopeInput) : scopeInput;
      scope = Array.isArray(parsedScope) ? parsedScope : null;
    } catch {
      scope = null;
    }
  }

  return {
    table: (inputsMap.get("table") as string) ?? "",
    operation: (inputsMap.get("operation") as string) ?? "",
    source: (inputsMap.get("source") as string) ?? "",
    isMultiple: (inputsMap.get("is_multiple") as boolean) ?? false,
    limit: (inputsMap.get("limit") as number) ?? 0,
    outputName: (inputsMap.get("output_name") as string) ?? "",
    logic_operator: (inputsMap.get("logic_operator") as string) ?? "AND",
    data: parsedData,
    filters,
    scope,
  };
};

const DataCenterContent = ({ execution }: Props) => {
  const inputs = useMemo(() => getExecutionInputs(execution), [execution]);

  const tableOperation = useMemo(
    () => inputs?.operation.toLowerCase() ?? "",
    [inputs?.operation],
  );

  const operationLabel = useMemo(
    () => OPERATION_LABELS[tableOperation] ?? tableOperation,
    [tableOperation],
  );

  const sourceLabel = useMemo(
    () => inputs?.source.toLowerCase() ?? "",
    [inputs?.source],
  );

  const outputs = useMemo(() => {
    if (!execution?.outputs || !inputs?.outputName) return null;

    const output = execution.outputs.find(
      (output) => output.name === inputs.outputName,
    );

    return output?.value as TableOutputs | null;
  }, [execution?.outputs, inputs?.outputName]);

  if (!inputs || !execution) {
    return (
      <div className="p-4 border border-error-quiet rounded-lg bg-error-silent flex gap-3 items-start">
        <Icon
          type="fas"
          name="exclamation-triangle"
          className="text-error py-2.5"
          size="sm"
          aria-label="Error"
        />
        <div>
          <p className="font-semibold text-error-loud">
            No se pudo cargar la información
          </p>
          <p className="text-sm text-error mt-1">
            No hay datos de ejecución disponibles para este nodo.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p>Estado del nodo: {getExecutionBadge(execution.status)}</p>
      <p>Se completó el: {formatDateTime(execution.ended_at || "")}</p>
      <div className="mt-2 border border-neutral rounded-lg p-4 bg-neutral-object flex flex-col gap-1">
        <p className="capitalize">
          Tabla: <span className="font-semibold">{inputs.table}</span>{" "}
          <Badge
            size="xs"
            shape="pill"
            variant={sourceLabel.includes("sdt") ? "action" : "neutral"}
          >
            {sourceLabel.includes("sdt") ? "local" : sourceLabel}
          </Badge>
        </p>
        <p className="capitalize">
          Operación: <span className="font-semibold">{operationLabel}</span>
        </p>
        {tableOperation === "read" && (
          <>
            <p className="capitalize">
              Múltiples resultados:{" "}
              <span className="font-semibold">
                {inputs.isMultiple ? "Sí" : "No"}
              </span>
            </p>
            {inputs.isMultiple && (
              <p>
                Cantidad de Resultados:{" "}
                <span className="font-semibold">{inputs.limit}</span>
              </p>
            )}
          </>
        )}
        <p>
          Nombre de salida:{" "}
          <span className="font-semibold">{inputs.outputName}</span>
        </p>
        {(tableOperation === "read" ||
          tableOperation === "update" ||
          tableOperation === "delete") &&
          inputs.scope && (
            <DataCenterConditionsList
              items={inputs.scope}
              title="Alcance"
              fieldLabels={SCOPE_FIELD_LABELS}
            />
          )}

        {(tableOperation === "read" ||
          tableOperation === "update" ||
          tableOperation === "delete") &&
          inputs.filters && (
            <DataCenterConditionsList
              items={inputs.filters}
              title="Condiciones"
              logicOperator={inputs.logic_operator}
            />
          )}

        {(tableOperation === "create" || tableOperation === "update") && (
          <DataCenterCreateValues
            data={inputs.data}
            operation={tableOperation}
          />
        )}
      </div>
      <DataCenterOutputTable outputs={outputs} />
    </div>
  );
};

const DataCenterCreateValues = ({
  data,
  operation,
}: {
  data: Record<string, unknown>;
  operation: "create" | "update";
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  return (
    <div>
      <div className="border border-neutral rounded-lg p-4 bg-neutral-object">
        <div className="flex items-center gap-1">
          <Button
            aria-label={isOpen ? "Cerrar" : "Abrir"}
            size="icon"
            variant="tertiary"
            onClick={() => setIsOpen(!isOpen)}
          >
            <Icon type="far" name={isOpen ? "chevron-up" : "chevron-down"} />
          </Button>
          <span>
            Valores a {operation === "create" ? "Insertar" : "Actualizar"}
          </span>
        </div>
        {isOpen && (
          <div className="mt-4 space-y-2">
            {Object.entries(data)
              .filter(([key]) => key.toLowerCase() !== "id")
              .map(([key, value]) => (
                <div
                  key={key}
                  className="flex items-start gap-2 py-2 border-b border-neutral-quiet last:border-b-0"
                >
                  <span className="font-semibold text-sm min-w-[120px] text-neutral-loud">
                    {key}:
                  </span>
                  <span className="text-sm text-neutral-text break-all flex-1">
                    {renderValue(value)}
                  </span>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface ConditionItem {
  id?: string;
  name: string;
  condition: string;
  value: string | null;
  type: string;
}

/**
 * Handles variables, booleans, null/undefined, objects, and strings
 */
const renderValue = (value: unknown): React.ReactNode => {
  // Handle null or undefined
  if (value === null || value === undefined) {
    return <span className="text-neutral-quiet">N/A</span>;
  }

  // Handle booleans
  if (typeof value === "boolean") {
    return value ? "Sí" : "No";
  }

  // Handle objects (excluding strings)
  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }

  // Handle strings - check for variables
  const stringValue = String(value);
  const hasVariable = stringValue.match(/\{\{.*?\}\}/g);

  if (hasVariable) {
    const variableName = stringValue.replace(/{{|}}/g, "").trim();
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge size="xs" variant="neutral">
              {variableName}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>Esta es una variable</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return <span className="break-all truncate">{stringValue}</span>;
};

/**
 * Display conditions or scope items
 * Used for filters and scope
 */
const DataCenterConditionsList = ({
  items,
  title,
  logicOperator,
  fieldLabels,
}: {
  items: ConditionItem[] | null;
  title: string;
  logicOperator?: string;
  fieldLabels?: Record<string, string>;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="border border-neutral rounded-lg p-4 bg-neutral-object">
        <div className="flex items-center gap-1">
          <Button
            aria-label={isOpen ? "Cerrar" : "Abrir"}
            size="icon"
            variant="tertiary"
            onClick={() => setIsOpen(!isOpen)}
          >
            <Icon type="far" name={isOpen ? "chevron-up" : "chevron-down"} />
          </Button>
          <span>{title}</span>
        </div>
        {isOpen && (
          <div className="mt-4 space-y-3">
            {items.map((item, index) => {
              const conditionLabel =
                CONDITION_LABELS[item.condition] || item.condition;
              const fieldLabel = fieldLabels?.[item.name] || item.name;

              return (
                <div
                  key={item.id || index}
                  className="p-3 border border-neutral-quiet rounded-lg bg-neutral-object-accent-silent"
                >
                  <div className="flex items-center gap-2 mb-2">
                    {index > 0 && logicOperator && (
                      <Badge size="xs" variant="info">
                        {logicOperator}
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="font-semibold text-sm min-w-[100px] text-neutral-loud">
                        Campo:
                      </span>
                      <span className="text-sm text-neutral-text">
                        {fieldLabel}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="font-semibold text-sm min-w-[100px] text-neutral-loud">
                        Condición:
                      </span>
                      <span className="text-sm text-neutral-text">
                        {conditionLabel}
                      </span>
                    </div>
                    {item.value && (
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-sm min-w-[100px] text-neutral-loud">
                          Valor:
                        </span>
                        <span className="text-sm text-neutral-text">
                          {renderValue(item.value)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

interface TableProps {
  outputs: TableOutputs | null;
}

const DataCenterOutputTable = ({ outputs }: TableProps) => {
  const columns: ColumnDef<Record<string, unknown>>[] = useMemo(() => {
    if (!outputs?.data || outputs.data.length === 0) return [];
    const headers = Object.keys(outputs.data[0]).filter(
      (key) => !["id"].includes(key.toLowerCase()),
    );
    return headers.map((header) => ({
      accessorKey: header,
      header: header,
      enableSorting: false,
      cell: ({ getValue }) => {
        const value = getValue();
        if (typeof value === "boolean") {
          return value ? "Sí" : "No";
        }
        if (value === null || value === undefined) {
          return "-";
        }
        return String(value);
      },
    }));
  }, [outputs?.data]);

  return outputs?.data && outputs.data.length > 0 ? (
    <div className="py-4">
      <Heading variant="h6">Resultados de la operación</Heading>
      <div className="mb-2 p-3 border border-contextual-warning-quiet rounded-lg bg-surface-warning-accent-quiet flex gap-2">
        <Icon
          type="fas"
          name="info-circle"
          className="text-warning-quiet py-2.5"
          size="sm"
          aria-label="Información"
        />
        <p className="text-sm text-warning-loud">
          <span className="font-semibold">Nota:</span> Los datos mostrados
          corresponden al momento de esta ejecución. Los usuarios podrían ver
          información diferente si los datos se modifican posteriormente al
          momento de llenar el formulario.
        </p>
      </div>
      <TableData
        columns={columns}
        data={outputs.data}
        stripped
        wrapperClassName="overflow-x-auto"
      />
    </div>
  ) : (
    <div className="py-4">
      <Heading variant="h6">Resultados de la operación</Heading>
      {outputs?.success ? (
        <div className="mb-2 p-3 border rounded-lg border-success-quiet bg-surface-sucess-accent flex gap-2">
          <Icon
            type="fas"
            name="circle-check"
            className="text-success opacity-70 py-2.5"
            size="sm"
            aria-label="Información"
          />
          <div>
            <p>{outputs?.message}</p>
            <p>
              Registros afectados:{" "}
              {outputs?.updated_count || outputs?.deleted_count || 0}
            </p>
          </div>
        </div>
      ) : (
        <div className="mb-2 p-3 border rounded-lg border-danger-quiet bg-surface-danger-accent-quiet flex gap-2 items-center">
          <Icon
            type="fas"
            name="circle-exclamation"
            className="text-danger opacity-70 py-2.5"
            size="sm"
            aria-label="Información"
          />
          <div>
            <p>Hubo un problema al procesar la operación</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataCenterContent;
